import axiosInstance from "../../components/api/axiosInstance";

/** Normalize a file row from POST /file or issue payload */
export function normalizeIssueFileRecord(raw) {
  if (!raw || typeof raw !== "object") return null;
  const rec = {
    fileId: raw.File_Id ?? raw.file_id,
    fileName: raw.File_Name ?? raw.file_name ?? "Attachment",
    filePath: raw.File_Path ?? raw.file_path ?? "",
    fileUrl: raw.File_URL ?? raw.file_url ?? "",
    issueId: raw.Issue_Id ?? raw.issue_id,
    uploadedOn: raw.Uploaded_On ?? raw.uploaded_on,
    uploadedBy: raw.Uploaded_By ?? raw.uploaded_by
  };
  if (rec.fileId == null && !rec.fileUrl) return null;
  return rec;
}

/** Collect file attachments from GET /issues/:id (and similar) payloads */
export function extractIssueFilesFromIssue(issue) {
  if (!issue) return [];
  const raw =
    issue.Files ??
    issue.files ??
    issue.File_List ??
    issue.file_list ??
    issue.Attachments ??
    issue.attachments ??
    issue.Issue_Files ??
    issue.issue_files ??
    [];
  if (!Array.isArray(raw)) return [];
  return raw.map(normalizeIssueFileRecord).filter(Boolean);
}

/**
 * POST multipart: issue_id, file — same as Postman /file
 */
export async function uploadIssueFile(issueId, file) {
  const id = Number(issueId);
  if (!Number.isFinite(id)) {
    throw new Error("Invalid issue id");
  }
  if (!file || !(file instanceof Blob)) {
    throw new Error("No file selected");
  }
  const formData = new FormData();
  formData.append("issue_id", String(id));
  formData.append("file", file);
  const response = await axiosInstance.post("/file", formData);
  const data = response.data?.data ?? response.data;
  return normalizeIssueFileRecord(data) || data;
}

/**
 * DELETE /delete-file/:file_id — response e.g. { File_Id, message: "Deleted" }
 */
export async function deleteIssueFile(fileId) {
  const id = Number(fileId);
  if (!Number.isFinite(id) || id <= 0) {
    throw new Error("Invalid file id");
  }
  const customPath = process.env.REACT_APP_ISSUE_FILE_DELETE_PATH;
  const path =
    customPath && String(customPath).trim()
      ? String(customPath).replace(/\{id\}/g, String(id)).replace(/:id/g, String(id))
      : `/delete-file/${id}`;

  try {
    const response = await axiosInstance.delete(path);
    return response.data?.data ?? response.data;
  } catch (err) {
    if (err?.response?.status === 405) {
      const response = await axiosInstance.post(path);
      return response.data?.data ?? response.data;
    }
    throw err;
  }
}

function parseIssueFilesResponse(res) {
  const body = res?.data;
  const arr = Array.isArray(body)
    ? body
    : Array.isArray(body?.data)
      ? body.data
      : Array.isArray(body?.files)
        ? body.files
        : [];
  return (arr || []).map(normalizeIssueFileRecord).filter(Boolean);
}

/**
 * 404/405 resolve instead of reject → axios error interceptor won't spam the console
 * while we probe routes. Network tab may still show 404 rows (browser behavior).
 */
const validateStatusProbe = (status) =>
  (status >= 200 && status < 300) || status === 404 || status === 405;

/**
 * GET list of files for an issue. Default: GET /files?issue_id= (per backend contract).
 * Optional: REACT_APP_ISSUE_FILES_LIST_PATH=/your/path (tried first).
 * If the route is missing on the API host, returns [] (no thrown error).
 */
export async function fetchIssueFiles(issueId) {
  const id = Number(issueId);
  if (!Number.isFinite(id)) return [];

  const attempts = [];
  const customListPath = process.env.REACT_APP_ISSUE_FILES_LIST_PATH;
  if (customListPath && String(customListPath).trim()) {
    attempts.push({ url: String(customListPath).trim(), params: { issue_id: id } });
  }

  attempts.push(
    { url: "/files", params: { issue_id: id } },
    { url: "/files", params: { Issue_Id: id } },
    { url: `/issue-files/${id}` }
  );

  for (const { url, params } of attempts) {
    const res = await axiosInstance.get(url, {
      ...(params != null ? { params } : {}),
      validateStatus: validateStatusProbe
    });

    if (res.status === 404 || res.status === 405) {
      continue;
    }
    if (res.status >= 200 && res.status < 300) {
      return parseIssueFilesResponse(res);
    }
  }

  return [];
}

/** Format API Due_Date for `<input type="date" />` (YYYY-MM-DD). */
export function issueDueDateToInputValue(value) {
  if (value == null || value === "") return "";
  const s = String(value).trim();
  if (/^\d{4}-\d{2}-\d{2}$/.test(s)) return s;
  if (/^\d{4}-\d{2}-\d{2}/.test(s)) return s.slice(0, 10);
  const d = new Date(s);
  if (Number.isNaN(d.getTime())) return "";
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

/**
 * POST /create-issue body: optional Sub_Task_Id, Due_Date, Actual_End_Date omitted when empty; keys match backend (PascalCase).
 */
export function sanitizeCreateIssuePayload(body) {
  if (!body || typeof body !== "object") return body;
  const out = { ...body };
  if (out.Sub_Task_Id == null || out.Sub_Task_Id === "" || !Number.isFinite(Number(out.Sub_Task_Id))) {
    delete out.Sub_Task_Id;
  } else {
    out.Sub_Task_Id = Number(out.Sub_Task_Id);
  }
  if (out.Due_Date == null || String(out.Due_Date).trim() === "") {
    delete out.Due_Date;
    delete out.due_date;
  } else {
    out.Due_Date = String(out.Due_Date).trim().slice(0, 10);
  }
  if (out.Actual_End_Date == null || String(out.Actual_End_Date).trim() === "") {
    delete out.Actual_End_Date;
    delete out.actual_end_date;
    delete out.Updated_Date;
    delete out.updated_date;
    delete out.Updated_On;
    delete out.updated_on;
  } else {
    const ae = String(out.Actual_End_Date).trim().slice(0, 10);
    out.Actual_End_Date = ae;
    out.actual_end_date = ae;
    out.Updated_Date = ae;
    out.updated_date = ae;
    out.Updated_On = ae;
    out.updated_on = ae;
  }
  if (out.Due_Date != null && String(out.Due_Date).trim() !== "") {
    out.due_date = String(out.Due_Date).trim().slice(0, 10);
  }
  return out;
}

/** Map GET payloads so Actual end date shows when API only returns Updated_On / snake_case aliases. */
export function normalizeIssueFromApi(raw) {
  if (!raw || typeof raw !== "object") return raw;
  const o = { ...raw };
  const end =
    o.Actual_End_Date ??
    o.actual_end_date ??
    o.Updated_Date ??
    o.updated_date ??
    o.Updated_On ??
    o.updated_on ??
    o.Actual_Date ??
    o.actual_date ??
    o.ActualEndDate ??
    o.actualEndDate ??
    null;
  if (end != null && String(end).trim() !== "") {
    const s = String(end).trim();
    if (o.Actual_End_Date == null || String(o.Actual_End_Date).trim() === "") o.Actual_End_Date = s.slice(0, 10);
    if (o.actual_end_date == null || String(o.actual_end_date).trim() === "") o.actual_end_date = s.slice(0, 10);
  }
  return o;
}

function unwrapIssueGetBody(body) {
  if (!body || typeof body !== "object") return body;
  const inner = body.issue ?? body.Issue;
  if (inner && typeof inner === "object" && (inner.Issue_Id != null || inner.issue_id != null)) {
    return inner;
  }
  return body;
}

/** Persist actual end via keys the API maps to DB (incl. Updated_On). */
export function mirrorIssueDateKeysOnPayload(payload) {
  if (!payload || typeof payload !== "object") return payload;
  if (payload.Due_Date != null && String(payload.Due_Date).trim() !== "") {
    const d = String(payload.Due_Date).trim().slice(0, 10);
    payload.Due_Date = d;
    payload.due_date = d;
  }
  if (payload.Actual_End_Date != null && String(payload.Actual_End_Date).trim() !== "") {
    const ae = String(payload.Actual_End_Date).trim().slice(0, 10);
    payload.Actual_End_Date = ae;
    payload.actual_end_date = ae;
    payload.Updated_Date = ae;
    payload.updated_date = ae;
    payload.Updated_On = ae;
    payload.updated_on = ae;
  }
  return payload;
}

/**
 * Normalize id from POST /create-issue — response shapes vary (flat, nested under data/issue, arrays).
 */
export function extractCreatedIssueId(created, depth = 0) {
  if (created == null || depth > 4) return null;
  const obj = Array.isArray(created) ? created[0] : created;
  if (!obj || typeof obj !== "object") return null;

  const raw =
    obj.Issue_Id ??
    obj.issue_id ??
    obj.IssueId ??
    obj.issueId ??
    obj.insertId ??
    obj.last_insert_id ??
    obj.ID ??
    obj.Id;
  const n = Number(raw);
  if (Number.isFinite(n) && n > 0) return n;

  const inner = obj.data ?? obj.issue ?? obj.result ?? obj.payload;
  if (inner != null && inner !== obj) {
    const nested = extractCreatedIssueId(inner, depth + 1);
    if (nested != null) return nested;
  }

  const lastResort = Number(obj.id);
  if (Number.isFinite(lastResort) && lastResort > 0) return lastResort;

  return null;
}

export async function createIssue(payload) {
  const path =
    (process.env.REACT_APP_ISSUE_CREATE_PATH && String(process.env.REACT_APP_ISSUE_CREATE_PATH).trim()) ||
    "/create-issue";
  const body = sanitizeCreateIssuePayload(payload);
  const response = await axiosInstance.post(path, body);
  return response.data?.data ?? response.data;
}

export async function fetchIssues({ taskId, subTaskId, testCaseId, assignedUserId } = {}) {
  const params = {};
  if (taskId != null && taskId !== "" && Number.isFinite(Number(taskId))) {
    params.task_id = Number(taskId);
  }
  if (subTaskId != null && subTaskId !== "" && Number.isFinite(Number(subTaskId))) {
    params.sub_task_id = Number(subTaskId);
  }
  if (testCaseId != null && testCaseId !== "" && Number.isFinite(Number(testCaseId))) {
    params.test_case_id = Number(testCaseId);
  }
  if (assignedUserId != null && assignedUserId !== "" && Number.isFinite(Number(assignedUserId))) {
    params.assigned_user_id = Number(assignedUserId);
  }

  const response = await axiosInstance.get("/issues", { params });
  const raw = response.data;
  const arr = Array.isArray(raw)
    ? raw
    : Array.isArray(raw?.data)
      ? raw.data
      : Array.isArray(raw?.issues)
        ? raw.issues
        : [];
  const list = Array.isArray(arr) ? arr : [];
  return list.map((row) => normalizeIssueFromApi(row));
}

export async function fetchIssueById(issueId) {
  if (issueId == null || issueId === "" || !Number.isFinite(Number(issueId))) return null;
  const response = await axiosInstance.get(`/issues/${Number(issueId)}`);
  let data = response.data?.data ?? response.data;
  data = unwrapIssueGetBody(data);
  return normalizeIssueFromApi(data);
}

export async function updateIssue(issueId, payload) {
  if (issueId == null || issueId === "" || !Number.isFinite(Number(issueId))) {
    throw new Error("Invalid issue id");
  }
  const body = mirrorIssueDateKeysOnPayload({ ...payload });
  const response = await axiosInstance.put(`/update-issue/${Number(issueId)}`, body);
  const data = response.data?.data ?? response.data;
  return normalizeIssueFromApi(data);
}

export async function deleteIssue(issueId) {
  if (issueId == null || issueId === "" || !Number.isFinite(Number(issueId))) {
    throw new Error("Invalid issue id");
  }
  try {
    const response = await axiosInstance.delete(`/delete-issue/${Number(issueId)}`);
    return response.data?.data ?? response.data;
  } catch (err) {
    // Some backends expose delete as POST/GET even if named delete-*
    if (err?.response?.status === 405) {
      const response = await axiosInstance.post(`/delete-issue/${Number(issueId)}`);
      return response.data?.data ?? response.data;
    }
    throw err;
  }
}

export async function fetchAllUsers() {
  const response = await axiosInstance.get("/users");
  const raw = response.data;
  const arr = Array.isArray(raw)
    ? raw
    : Array.isArray(raw?.data)
      ? raw.data
      : Array.isArray(raw?.users)
        ? raw.users
        : [];

  return (arr || [])
    .map((u) => {
      const id = u.user_id ?? u.id ?? u.User_Id;
      const name =
        (u.user_name ?? u.User_Name) ||
        `${u.first_name ?? u.First_Name ?? ""} ${u.last_name ?? u.Last_Name ?? ""}`.trim() ||
        u.email ||
        "—";
      return { id, name: (name || "—").trim() || "—" };
    })
    .filter((x) => x.id != null);
}

export async function fetchIssueStatuses() {
  const response = await axiosInstance.get("/issue-statuses");
  const raw = response.data;
  const arr = Array.isArray(raw)
    ? raw
    : Array.isArray(raw?.data)
      ? raw.data
      : Array.isArray(raw?.statuses)
        ? raw.statuses
        : [];

  return (arr || [])
    .map((s) => ({
      id: s.Status_Id ?? s.status_id ?? s.id,
      name: s.Status_Name ?? s.status_name ?? s.name ?? "—",
      description: s.Description ?? s.description ?? null
    }))
    .filter((x) => x.id != null);
}

